<script>
import {defineComponent} from 'vue'
import api from "@/api/api"
import {formatIsoToCustom} from "@/common/formatToIso";
import {processObjectNullValues} from "@/common/processObjectNullValues";
import TicketLayouts from "@/components/Tickets/layouts/TicketLayouts.vue";
import UserSelect from "@/components/Tickets/ui/user-select.vue";

export default defineComponent({
  name: "TicketGroups",
  components: {UserSelect, TicketLayouts},
  created() {
    this.debouncedSearch = this.debounce((searchValue) => {
      this.currentAdminsPage = 1
      this.getAdminUsers(1, false, searchValue)
    }, 500)
  },
  data() {
    return {
      groupParams: {
        group_name: "",
        enabled: true,
      },
      allUserAdmins: [],
      allUserAdminsTotal: 0,
      currentAdminsPage: 1,
      searchAdmin: "",
      search: '',
      ticketGroups: [],
      isLoading: false,
      sidebarFields: [],
      isSidebarLoading: false,
      sidebarTitle: '',
      selectedGroup: {},
      selectedUserForGroup: null,
      sidebarMode: 'form', // 'form' или 'custom'
      groupUsers: [],
      fields: [
        {key: 'id', label: '№', sortable: true},
        {key: 'date', label: 'Дата', sortable: true},
        {key: 'group_name', label: 'Название группы'},
        {key: 'enabled', label: 'Включено',},
        {key: 'username', label: 'Кем создан',},
        {key: 'users_count', label: 'Кол-ство пользователей',},
        {key: 'actions', label: 'Действия',},
      ]
    }
  },
  computed: {
    rows() {
      const query = this.search.trim().toLowerCase()
      if (!query) return this.ticketGroups || []
      return (this.ticketGroups || []).filter(item =>
          [item.id, item.group_name].some(field => String(field ?? '').toLowerCase().includes(query)))
    },
  },
  methods: {
    onUserSelected(user) {
      this.selectedUserForGroup = user
    },
    handleSidebarClose() {
      this.groupUsers = []
      this.selectedUserForGroup = null
      this.selectedGroup = {}
    },
    async getAdminUsers(page = 1, append = false, search = "") {
      this.isLoading = true;
      try {
        const res = await api.users.index({
          "onlyModerators": "true",
          "sort": "role",
          "search": search || null,
          "page": page,
          "per_page": 20
        })
        const newUsers = res.data?.users
            // ?.filter(user => user.roles.find(role => role.name === 'admin'))
            ?.map(user => ({
              value: +user?.id,
              text: user?.email || user?.username || ''
            })) || []
        if (append) {
          this.allUserAdmins = [...this.allUserAdmins, ...newUsers]
        } else {
          this.allUserAdmins = newUsers
        }
        this.allUserAdminsTotal = res.data?.total || 0
        this.currentAdminsPage = page
        this.isLoading = false;
        return newUsers.length > 0
      } catch (e) {
        this.$bvToast.toast(e.response.message || e.response.data.message || 'Произошла ошибка', {
          title: "Ошибка сервера",
          variant: "danger",
        })
        return false
      } finally {
        this.isLoading = false;
      }
    },
    async loadMoreAdmins(page, search) {
      try {
        const hasMore = await this.getAdminUsers(page, true, search)
        return hasMore
      } catch (e) {
        console.error('Error loading more admins:', e)
        return false
      }
    },
    async getTicketGroups() {
      this.isLoading = true;
      try {
        const res = await api.tickets.getListUserGroups({})
        this.ticketGroups = res.data?.data.map((type) => (processObjectNullValues({
          id: type.id,
          date: formatIsoToCustom(type.created_at),
          group_name: type.group_name,
          enabled: type.enabled ? 'Да' : "Нет",
          username: type.created_by?.username || type.created_by?.email,
          description: type.description,
          users_count: type.users_count,
        })));

        this.isLoading = false;
      } catch (e) {
        console.log(e)
        this.$bvToast.toast(e.response.message || e.response.data.message || 'Произошла ошибка', {
          title: "Ошибка сервера",
          variant: "danger",
        })
      } finally {
        this.isLoading = false;
      }
    },
    async createGroup() {
      if (this.groupParams.group_name === "") {
        this.$bvToast.toast('Введите название', {
          title: "Валидация",
          variant: "danger",
        })
        return false
      }
      try {
        const res = await api.tickets.createUserGroup(this.groupParams)
        const newGroup = res.data?.data
        const formattedGroup = processObjectNullValues({
          id: newGroup.id,
          date: formatIsoToCustom(newGroup.created_at),
          group_name: newGroup.group_name,
          enabled: newGroup.enabled ? 'Да' : "Нет",
          username: newGroup.created_by?.username || newGroup.created_by?.email,
          description: newGroup.description,
        })
        this.ticketGroups.push(formattedGroup)
        this.$bvModal.hide('create-group')
        this.groupParams.group_name = ''
      } catch (e) {
        console.log(e)
        this.$bvToast.toast(e.response.message || e.response.data.message || 'Произошла ошибка', {
          title: "Ошибка сервера",
          variant: "danger",
        })
      }
    },
    async deleteGroup(group) {
      try {
        if (group && group.id) {
          await api.tickets.deleteUserGroup(group?.id)
        }
        this.ticketGroups = this.ticketGroups.filter(item => item.id !== group.id)
      } catch (e) {
        console.log(e)
        this.$bvToast.toast(e.response.data.message || e.response.message || 'Произошла ошибка', {
          title: "Ошибка сервера",
          variant: "danger",
        })
      }
    },
    async handleSaveGroup(formData) {
      this.isSidebarLoading = true
      try {
        const res = await api.tickets.updateUserGroup(this.selectedGroup?.id, {
          enabled: formData.enabled,
          group_name: formData.group_name,
          description: formData.description
        })
        const updatedData = res.data?.data || formData
        const index = this.ticketGroups.findIndex(type => type.id === formData.id)
        if (index !== -1) {
          const oldItem = this.ticketGroups[index]
          this.$set(this.ticketGroups, index, processObjectNullValues({
            id: updatedData.id,
            date: oldItem.date,
            group_name: updatedData.group_name,
            enabled: updatedData.enabled ? "Да" : "Нет",
            username: oldItem.username,
            _original: updatedData
          }))
        }
        this.$bvToast.toast('Группа успешно обновлена', {
          title: "Успех",
          variant: "success",
        })
        this.$root.$emit('bv::toggle::collapse', 'sidebar-right')
        this.selectedGroup = null
      } catch (e) {
        console.log(e)
        this.$bvToast.toast(e.response.data.message || e.response.message || 'Произошла ошибка', {
          title: "Ошибка сервера",
          variant: "danger",
        })
      }
    },
    openEditSidebar(item) {
      this.sidebarMode = 'form'
      this.sidebarTitle = 'Редактирование группы'
      this.sidebarFields = [
        {
          key: 'group_name',
          label: 'Название группы',
          type: 'text',
          placeholder: 'Введите название',
          required: true
        },
        {
          key: 'enabled',
          label: 'Включено',
          type: 'checkbox',
          default: true
        }
      ]
      this.selectedGroup = {
        id: item.id,
        group_name: item.group_name,
        enabled: item.enabled
      }
      this.$root.$emit('bv::toggle::collapse', 'sidebar-right')
    },
    async openUsersSidebar(item) {
      this.sidebarMode = 'custom'
      this.sidebarTitle = 'Пользователи группы'
      this.selectedGroup = item
      this.isSidebarLoading = true
      this.$root.$emit('bv::toggle::collapse', 'sidebar-right')

      try {
        const res = await api.tickets.getListGroupUsers(item.id)
        this.groupUsers = res.data?.data || []
      } catch (e) {
        console.log(e)
        this.groupUsers = []
        this.$bvToast.toast('Ошибка загрузки пользователей', {
          title: "Ошибка",
          variant: "danger",
        })
      } finally {
        this.isSidebarLoading = false
      }
    },
    async addUser() {
      try {
        const res = await api.tickets.addUserToGroup(this.selectedGroup.id, {
          user_id: this.selectedUserForGroup.value || null
        })
        this.groupUsers.push(res.data?.data)
        this.$bvToast.toast('Пользователь был успешно добавлен', {
          title: "Успех!",
          variant: "success",
        })
        this.selectedUserForGroup = null
        this.$refs.userSelect?.clear()
        const users = (await api.tickets.getListGroupUsers(this.selectedGroup.id)).data
        this.groupUsers = users?.data || []
      } catch (e) {
        this.$bvToast.toast('Ошибка добавления пользователя', {
          title: "Ошибка",
          variant: "danger",
        })
      }
    },
    async removeUserFromGroup(userId) {
      try {
        await api.tickets.removeUserFromGroup(this.selectedGroup.id, userId)
        this.groupUsers = this.groupUsers.filter(u => u.id !== userId)
        this.$bvToast.toast('Пользователь удален из группы', {
          title: "Успех",
          variant: "success",
        })
      } catch (e) {
        console.log(e)
        this.$bvToast.toast('Ошибка удаления пользователя', {
          title: "Ошибка",
          variant: "danger",
        })
      }
    },
    debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    },
  },
  mounted() {
    this.getTicketGroups()
    this.getAdminUsers(1, false)
  },
  watch: {
    searchAdmin(newVal) {
      this.debouncedSearch(newVal)
    }
  }
})
</script>

<template>
  <TicketLayouts
      :title="sidebarMode === 'custom' ? 'Пользователи группы' : 'Редактирование группы'"
      @sidebar-save="handleSaveGroup"
      @sidebar-close="handleSidebarClose"
      :sidebar-fields="sidebarFields"
      :sidebar-loading="isSidebarLoading"
      :sidebar-item="selectedGroup"
      :sidebar-mode="sidebarMode"
  >
    <div class="tr">
      <Breadcrumbs main="Тикеты" title="Группы поддержки"/>

      <div class="container-fluid tr-wrap">
        <div class="tr-toolbar">
          <div class="tr-search">
            <i class="fa fa-search tr-search__icon"></i>
            <input type="search" class="tr-search__input" v-model="search"
                   placeholder="ID или название группы…" @keydown.esc.prevent="search = ''"/>
            <button v-if="search" type="button" class="tr-search__clear" @click="search = ''">
              <i class="fa fa-times"></i>
            </button>
          </div>

          <span class="tr-spacer"></span>

          <button type="button" class="tr-btn-primary" v-b-modal.create-group>
            <i class="fa fa-plus"></i> Создать группу
          </button>
        </div>

        <div class="tr-panel">
          <div v-if="!rows.length" class="tr-empty">
            <div class="tr-empty__art"><i class="fa fa-users"></i></div>
            <div class="tr-empty__title">{{ search ? 'Ничего не нашлось' : 'Групп нет' }}</div>
            <div class="tr-empty__text">
              {{ search ? 'Попробуйте изменить запрос.' : 'Группы определяют, кто из сотрудников видит обращения.' }}
            </div>
            <button type="button" class="tr-btn-primary" v-b-modal.create-group>
              <i class="fa fa-plus"></i> Создать группу
            </button>
          </div>

          <div v-else class="tr-table-wrap">
            <table class="tr-table">
              <thead>
              <tr>
                <th>ID</th>
                <th>Группа</th>
                <th>Включена</th>
                <th>Сотрудников</th>
                <th>Кем создана</th>
                <th>Создана</th>
                <th></th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="item in rows" :key="'gr' + item.id">
                <td><span class="tr-id">#{{ item.id }}</span></td>
                <td><span class="tr-name">{{ item.group_name }}</span></td>
                <td>
                  <span class="tr-badge" :class="item.enabled === 'Да' ? 'tr-badge--ok' : 'tr-badge--off'">
                    {{ item.enabled === 'Да' ? 'Включена' : 'Выключена' }}
                  </span>
                </td>
                <td><span class="tr-badge tr-badge--primary">{{ item.users_count }}</span></td>
                <td>{{ item.username }}</td>
                <td class="tr-num">{{ item.date }}</td>
                <td>
                  <div class="tr-actions">
                    <button type="button" class="tr-act" @click="openUsersSidebar(item)"
                            v-b-tooltip.hover.top="'Пользователи группы'">
                      <i class="fa fa-users"></i>
                    </button>
                    <button type="button" class="tr-act" @click="openEditSidebar(item)"
                            v-b-tooltip.hover.top="'Редактировать'">
                      <i class="fa fa-pencil"></i>
                    </button>
                    <button type="button" class="tr-act tr-act--danger" @click="deleteGroup(item)"
                            v-b-tooltip.hover.top="'Удалить'">
                      <i class="fa fa-trash-o"></i>
                    </button>
                  </div>
                </td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <template #sidebar-custom-content="{item, close}">
      <div>
        <div class="mb-3 pb-3 border-bottom">
          <h5 class="mb-2">{{ item.group_name }}</h5>
          <p class="text-muted mb-0">
            <i class="fa fa-users mr-1"></i>
            Всего пользователей: <strong>{{ groupUsers.length }}</strong>
          </p>
        </div>

        <div v-if="groupUsers.length > 0" style="max-height: 60vh; overflow-y: auto;">
          <b-list-group>
            <b-list-group-item
                v-for="user in groupUsers"
                :key="user.id"
                class="d-flex justify-content-between align-items-center"
            >
              <div>
                <strong>{{ user.username || user.email }}</strong>
                <br>
                <small class="text-muted">{{ user.email }}</small>
              </div>
              <b-button variant="outline-danger" size="sm" @click="removeUserFromGroup(user.id)">
                Удалить
              </b-button>
            </b-list-group-item>
          </b-list-group>
        </div>

        <div v-else class="text-center text-muted py-5">
          <i class="fa fa-users fa-3x mb-3 opacity-50"></i>
          <p>В группе пока нет пользователей</p>
        </div>

        <user-select
            ref="userSelect"
            placeholder="Выберите пользователя"
            :on-load-more="loadMoreAdmins"
            :options="allUserAdmins"
            @select="onUserSelected"
            @search="searchAdmin = $event"
        />
        <b-button variant="primary" class="w-100 mt-3"
                  :disabled="selectedUserForGroup === null || isLoading"
                  @click="addUser">
          <template v-if="isLoading">
            <b-spinner small></b-spinner>
            <span class="sr-only">Loading...</span>
          </template>
          <template v-else>Добавить</template>
        </b-button>
        <b-button variant="secondary" class="w-100 mt-3" @click="close">Закрыть</b-button>
      </div>
    </template>

    <b-modal id="create-group" title="Новая группа" hide-footer>
      <b-form @submit.prevent="createGroup">
        <label class="font-weight-bold">Название</label>
        <b-form-input v-model="groupParams.group_name" placeholder="Например: Первая линия"/>
        <b-form-checkbox class="mt-3" v-model="groupParams.enabled">Включена</b-form-checkbox>
        <b-button class="mt-3 w-100" variant="primary" type="submit">Создать</b-button>
      </b-form>
    </b-modal>
  </TicketLayouts>
</template>

<style scoped lang="scss">
@import "~@/assets/scss/ticket-ref.scss";
</style>
